import React, {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {useNavigate} from "react-router-dom";
import Bottom from "../Bottom";
import Top from "../Top";
import GaugeChart from "../../components/GaugeChart";
import LineChart from "../../components/LineChart";
import DeviceReadingList from "../../components/DeviceReadingList";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import StressGaugeBar from "../../components/StressGaugeBar";
import heartbeatIcon from "../../assets/icon/heartbeat.gif";
import * as util from "../../utils/util";
import {
  DeviceInfo,
  getDeviceMetrics,
  getGaugeValue,
  getMetricStatus,
  getPrimaryMetric,
  getSummaryValue
} from "../../utils/deviceInfo";
import {
  arrhythmiaOf,
  latestOf,
  latestReadingOf,
  readingsOf,
  stressLatestOf,
  trendOf,
  useHealthDevice,
} from "../../utils/healthDeviceApi";

/** 추이 그래프에 보여줄 일수 */
const TREND_DAY_COUNT = 7;

const HistoryDeviceEcgDetail = (props: { device: DeviceInfo }) => {
  const device = props.device;
  const navigate = useNavigate();
  const [dayValue, setDayValue] = useState(0);

  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const metrics = getDeviceMetrics(device.id);

  // AP_009_004 — ecgList(그날 기록)와 daily(일자별 평균 심박)
  const { data: deviceData } = useHealthDevice(dayValue);
  const trend = trendOf(deviceData, device, TREND_DAY_COUNT, dayValue);

  // 심박은 하루에 여러 번 기록 가능 - 최상단 / 그래프는 마지막(최신) 기록 기준
  const readings = readingsOf(deviceData, device);
  const latestReading = latestReadingOf(deviceData, device);
  const latest = latestOf(deviceData, device, dayValue);

  const primary = getPrimaryMetric(device.id);
  const summary = getSummaryValue(device.id, latest);
  const status = getMetricStatus(primary, latest[primary.key]);
  const gaugeValue = getGaugeValue(primary, latest[primary.key]);

  // 스트레스 지수 — AP_009_004 ecgList 의 stress_index / stress_level (최신 1건).
  // 부정맥으로 기록된 건은 둘 다 null 로 오므로 목록에 아예 담기지 않는다.
  // 그날 측정이 전부 부정맥이면 undefined 가 되고 영역이 숨는다.
  const stress = stressLatestOf(deviceData);

  // 부정맥은 이상이 확인된 기록만 노출 (정상이면 영역 자체를 숨김)
  const arrhythmiaList = arrhythmiaOf(deviceData);

  // 최근 7일 (오늘 포함) 날짜 라벨
  const trendLabels = trend.map((_, index) => {
    const date = util.getDateByCustom('yyyyMMdd', dayValue + index - (trend.length - 1));
    return date.slice(4, 6) + '.' + date.slice(6, 8);
  });

  const series = [{ curve: 'linear', data: trend }];
  const xAxis = [{ scaleType: 'point', data: trendLabels }];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev: any) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev: any) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 요약 */}
          <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: '10px 15px 8px 15px' }}>
              <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700 }}>{device.name}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: device.excluded ? '#bbb' : '#59b544', border: '1px solid', borderColor: device.excluded ? '#e0e0e0' : '#a5dd97', borderRadius: '10px', p: '2px 8px' }}>
                {device.excluded ? '실증 제외' : '연동됨'}
              </Typography>
            </Stack>
            <Divider />

            <Stack direction={'row'} justifyContent={'center'} sx={{ p: 1 }}>
              <Grid>
                <GaugeChart targetValue={gaugeValue} />
                <Typography component={'div'} sx={{ width: '100%', textAlign: 'center', color: status.color, fontSize: 16, fontWeight: 700, mb: 1 }}>{status.text}</Typography>
              </Grid>

              <Stack justifyContent={'center'} sx={{ m: 'auto' }}>
                <Avatar alt="" src={heartbeatIcon} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
              </Stack>

              <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
                <Stack direction={'column'} sx={{ ml: 'auto' }}>
                  <Typography component="div" sx={{ fontSize: 30, color: status.color, fontWeight: 700, textAlign: 'center' }}>
                    {summary.text}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 18, color: '#777', mb: 0, textAlign: 'center' }}>
                    {summary.unit}
                  </Typography>
                  {/* 하루 여러 건 중 마지막(최신) 기록임을 알 수 있도록 측정시간 노출 */}
                  {latestReading === undefined ? null : (
                    <Typography component="span" sx={{ fontSize: 12, color: '#999', textAlign: 'center' }}>
                      {latestReading.time} 기준
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* 심박 기록 */}
          <Accordion defaultExpanded sx={{ borderRadius: '15px', position: 'static', mb: '20px', '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="device-ecg-hr-content"
              id="device-ecg-hr-header"
              sx={{
                '&#device-ecg-hr-header': { minHeight: '55px' },
                '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 }
              }}
            >
              <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>심박 기록</Typography>
              <Typography sx={{ fontSize: 12, color: '#888', ml: 1, alignSelf: 'center' }}>{readings.length}건</Typography>
            </AccordionSummary>
            <Divider />

            <AccordionDetails>
              {/* 하루 동안 기록된 측정값 전체 (측정시간 + 측정값) */}
              <DeviceReadingList readings={readings} metrics={metrics} />
            </AccordionDetails>
          </Accordion>

          {/* 부정맥 기록 - 이상이 확인된 경우에만 노출 */}
          {arrhythmiaList.length === 0 ? null : (
            <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
              <Stack direction={'row'} alignItems={'center'} sx={{ p: '15px 15px 12px 15px' }}>
                <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>부정맥 기록</Typography>
                <Typography sx={{ fontSize: 12, color: '#888', ml: 1 }}>{arrhythmiaList.length}건</Typography>
              </Stack>
              <Divider />

              <Box sx={{ p: '0 15px 15px 15px' }}>
                {arrhythmiaList.map((arrhythmia, index) => (
                  // 같은 분에 두 건이 기록되면 시각만으로는 키가 겹친다
                  <Stack key={`${arrhythmia.time}-${index}`} direction={'row'} alignItems={'center'} sx={ArrhythmiaRowStyle}>
                     <Typography sx={{ fontSize: 14, color: '#777', mr: 'auto' }}>{arrhythmia.time}</Typography>
                    <WarningAmberRoundedIcon sx={{ color: '#df0000', fontSize: 20, mr: 1 }} />
                    <Typography sx={{ fontSize: 15, color: '#df0000', fontWeight: 700 }}>의심</Typography>
                   
                  </Stack>
                ))}
              </Box>
            </Box>
          )}

          {/* 스트레스 지수 - 그날 가장 최근 측정값 1건.
              기록이 없어도 영역은 남겨 빈 값으로 그린다 — 메뉴가 통째로 사라지면
              사용자는 기능이 없어진 것으로 본다. */}
          <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: '15px 15px 12px 15px' }}>
              <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>스트레스 지수</Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>
                {stress === undefined ? '기록 없음' : `${stress.time} 측정`}
              </Typography>
            </Stack>
            <Divider />
            {/* 단계는 지수(stress_index)로 판정한다 — 서버 stress_level(1~3)은 5단계로 늘릴 수 없다 */}
            <StressGaugeBar index={stress === undefined ? null : stress.index} />
          </Box>

          {/* 최근 7일 추이 */}
          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                {/* 하루 여러 건 중 마지막(최신) 기록을 그날 값으로 사용 */}
                <Typography component="div" sx={{ fontSize: 14, color: '#555' }}>최근 7일 {primary.label}</Typography>
                <Typography component="div" sx={{ fontSize: 11, color: '#999' }}>일별 최신 기록 기준</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2 }}>
                <Button sx={{ fontSize: 12, color: '#555' }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/HistoryDeviceMonthHistory', { state: { deviceId: device.id } }) }}>
                  월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />

            <Box sx={{ mt: -3.5 }}>
              <LineChart series={series} xAxis={xAxis} isReferenceLine={false} />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

const ArrhythmiaRowStyle = {
  mt: 1, p: '8px 12px', borderRadius: '10px'
};

export default HistoryDeviceEcgDetail;
